<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                element="InertiaLink"
                :pages="[
                    {
                        name: student.full_name,
                        href: meta.links.student_show,
                    },
                    {
                        name: $t('messages.attendance_calendar'),
                        href: null,
                    },
                ]"
            />
        </template>
        <div>
            <NavLinks :links="meta.navLinks.schoolYears" />
        </div>

        <div v-if="calendar" class="mt-5">
            <div class="grid-cols-2 md:grid-cols-3 md:gap-6">
                <div class="inline-block mr-5" v-for="month in calendar">
                    <div>
                        {{ month.month_name }}
                    </div>
                    <div>
                        <table>
                            <tbody>
                                <tr v-for="week in month.data">
                                    <td
                                        class="w-6 h-6 border-2 border-white relative"
                                        :class="weekday.class"
                                        :title="weekday.title"
                                        v-for="weekday in week"
                                    >
                                        <InertiaLink
                                            :href="weekday.url"
                                            class="block w-full h-full text-white"
                                            v-if="weekday.same_month && weekday.url"
                                        >
                                            {{ weekday.attendance_accumulated }}
                                        </InertiaLink>
                                        <div
                                            v-if="weekday.justified_absences"
                                            class="shrink-0 w-1.5 h-1.5 rounded-full bg-green-400 absolute bottom-0 right-0"
                                        ></div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="mt-5 flex">
                <div class="flex mr-3">
                    <div class="w-6 h-6 border-2 border-white bg-teal-600 mr-1"></div>
                    {{ $tc('messages.holidays', 1) }}
                </div>
                <div class="flex mr-3">
                    <div class="w-6 h-6 border-2 border-white bg-indigo-600 mr-1"></div>
                    100% {{ $t('messages.attendance') }}
                </div>
                <div class="flex mr-3">
                    <div class="w-6 h-6 border-2 border-white bg-red-300 mr-1"></div>
                    50% {{ $t('messages.attendance') }}
                </div>
                <div class="flex mr-3">
                    <div class="w-6 h-6 border-2 border-white bg-red-400 mr-1"></div>
                    0% {{ $t('messages.attendance') }}
                </div>
            </div>
        </div>
        <div v-else>
            <EmptyState picture />
        </div>

        <div v-if="attendances.length > 0">
            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.date') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.turn') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.absent') }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.actions') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(attendance, index) in attendances"
                                    :key="attendance.id"
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                >
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                        {{ attendance.date_display }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        {{ attendance.turn_display }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <div class="flex items-center space-x-3 lg:pl-2">
                                            <div v-if="attendance.absent">
                                                <div v-if="attendance.is_justified">
                                                    <div class="shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"></div>
                                                </div>
                                                <div v-else>
                                                    <div class="shrink-0 w-2.5 h-2.5 rounded-full bg-red-400"></div>
                                                </div>
                                            </div>
                                            <span>{{ attendance.justification }}</span>
                                        </div>
                                    </td>
                                    <td
                                        class="px-6 py-4 whitespace-nowrap text-right text-sm leading-5 font-medium flex gap-x-2 justify-end"
                                    >
                                        <InertiaSquareLinkButton action="show" :href="attendance.links.show" />
                                        <InertiaSquareLinkButton
                                            action="edit"
                                            :href="attendance.links.edit"
                                            v-if="attendance.can.edit"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="mt-3">
                <AppButton element="InertiaLink" :href="meta.links.justify">
                    {{ $t('messages.justify') }}
                </AppButton>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import NavLinks from '@/components/NavLinks.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';

export default {
    components: {
        InertiaSquareLinkButton,
        NavLinks,
        PanelPage,
        AppButton,
        EmptyState,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'calendar', 'student', 'attendances'],
};
</script>
